import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import { OrderEvent } from 'models/order-event';
import { useLazyQuery } from '@apollo/client';
import { commentsTemplate } from 'apollo/templates/order';
import { OrderInputType } from 'models/order-input/shared';
import AdditionalComments from 'components/order-flow/shared/additional-comments';
import InfoText from 'components/shared/info-text.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

const Comments: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const { order, setOrder } = useContext(LabOrderContext);
    const [oldComments, setOldComments] = useState<OrderEvent[]>([]);
    const [comment, setComment] = useState(order.comment ? order.comment : '');
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        if (order._id && order.inputType !== OrderInputType.NEW) {
            comments({ variables: { orderId: order._id } });
        }
    }, [order._id]);

    useEffect(() => {
        setComment(order.comment ? order.comment : '');
    }, [order.comment]);

    const [comments] = useLazyQuery(commentsTemplate, {
        onCompleted({ comments }) {
            setOldComments(comments);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleCommentBlur = () => {
        if (comment !== order.comment) {
            setOrder((prev) => ({ ...prev, comment: comment }));
        }
    };

    return (
        <Fragment>
            {order.historicalComments?.length > 0 && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        {translate('orders:ORIGINAL_ORDER_COMMENTS')}
                    </Typography>
                    <AdditionalComments additionalComments={order.historicalComments} />
                </Box>
            )}
            {oldComments.length ? (
                <Box>
                    {oldComments.map((comment, index) => (
                        <Box key={index} mb={2}>
                            <Box>
                                <Typography variant="subtitle2">
                                    {formatDate(new Date(comment.date), 'datetime')} - {comment.user?.fullName} (
                                    {translate('orders:VERSION')} {comment.version})
                                </Typography>
                            </Box>
                            <Box
                                dangerouslySetInnerHTML={{
                                    __html: (comment.message || '')
                                        // convert \n into <br/>
                                        .replace(/\n/g, '<br/>'),
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            ) : undefined}
            <TextField
                minRows={6}
                multiline
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                onBlur={handleCommentBlur}
                placeholder={translate('orders:COMMENT')}
                fullWidth
                data-test="constructions-step.comment-input"
            />
        </Fragment>
    );
};

export default Comments;
